import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import "./DateRange.css";
import { Calendar } from "react-date-range";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useBrand } from "../context/BrandContext";
import { sessionMonthAgenda } from "./funtionsBD/Mainservice";
import {
  formatCalendarDay,
  indexAgendaDays,
  monthKeyFromDate,
} from "../utils/sessionCalendarAgenda";
import { demoCalendarAgendaDays } from "../tour/tourDemoFixtures";

const AGENDA_REFRESH_EVENTS = ["sessionCreated", "smarttt-session-changed"];

export const DateRange = ({
  selectionRange,
  setSelectionRange,
  groupIds = [],
  tourAgenda = false,
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
  collapseOnSelect = true,
}) => {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const brandColor = brand?.color_primary || "#0877BC";
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(
    Boolean(defaultExpanded)
  );
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? Boolean(expandedProp) : uncontrolledExpanded;
  const setExpanded = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(expanded) : next;
      if (!isControlled) setUncontrolledExpanded(Boolean(value));
      onExpandedChange?.(Boolean(value));
    },
    [expanded, isControlled, onExpandedChange]
  );
  const [shownDate, setShownDate] = useState(
    () => selectionRange || new Date()
  );
  const [agendaByDate, setAgendaByDate] = useState({});
  const monthKey = monthKeyFromDate(shownDate);
  const groupKey = useMemo(
    () =>
      (Array.isArray(groupIds) ? groupIds : [])
        .map(String)
        .filter(Boolean)
        .sort()
        .join(","),
    [groupIds]
  );

  const localeCode = useMemo(() => {
    const raw = (i18n.language || localStorage.getItem("i18nextLng") || "en")
      .split("-")[0]
      .toLowerCase();
    if (raw === "gr") return "de";
    if (raw === "sp") return "es";
    return raw || "en";
  }, [i18n.language]);

  useEffect(() => {
    if (tourAgenda) {
      const [year, month] = (monthKey || "").split("-").map(Number);
      const anchor =
        Number.isFinite(year) && Number.isFinite(month)
          ? new Date(year, month - 1, 1)
          : shownDate || new Date();
      setAgendaByDate(indexAgendaDays(demoCalendarAgendaDays(anchor)));
      return undefined;
    }
    if (!monthKey) {
      setAgendaByDate({});
      return undefined;
    }
    const [year, month] = monthKey.split("-").map(Number);
    const gr_ids = groupKey ? groupKey.split(",") : [];
    let cancelled = false;
    sessionMonthAgenda({
      year,
      month,
      gr_ids,
    })
      .then((res) => {
        if (cancelled) return;
        setAgendaByDate(indexAgendaDays(res?.data?.days || []));
      })
      .catch(() => {
        if (!cancelled) setAgendaByDate({});
      });
    return () => {
      cancelled = true;
    };
  }, [groupKey, monthKey, tourAgenda, shownDate]);

  useEffect(() => {
    if (tourAgenda) return undefined;
    const refresh = () => {
      if (!monthKey) return;
      const [year, month] = monthKey.split("-").map(Number);
      const gr_ids = groupKey ? groupKey.split(",") : [];
      sessionMonthAgenda({
        year,
        month,
        gr_ids,
      })
        .then((res) => setAgendaByDate(indexAgendaDays(res?.data?.days || [])))
        .catch(() => {});
    };
    AGENDA_REFRESH_EVENTS.forEach((name) =>
      window.addEventListener(name, refresh)
    );
    return () => {
      AGENDA_REFRESH_EVENTS.forEach((name) =>
        window.removeEventListener(name, refresh)
      );
    };
  }, [groupKey, monthKey, tourAgenda]);

  const handleselect = (e) => {
    setSelectionRange(e);
    if (collapseOnSelect) setExpanded(false);
  };

  const dayContentRenderer = useCallback(
    (date) => {
      const key = formatCalendarDay(date);
      const day = agendaByDate[key];
      const kinds = Array.isArray(day?.kinds) ? day.kinds : [];
      return (
        <span className="rdrDayAgenda">
          <span>{moment(date).date()}</span>
          {kinds.length ? (
            <span className="rdrDayAgendaDots" aria-hidden="true">
              {kinds.includes("group") ? (
                <span className="rdrDayAgendaDot rdrDayAgendaDot--group" />
              ) : null}
              {kinds.includes("private") ? (
                <span className="rdrDayAgendaDot rdrDayAgendaDot--private" />
              ) : null}
            </span>
          ) : (
            <span className="rdrDayAgendaDots rdrDayAgendaDots--empty" />
          )}
        </span>
      );
    },
    [agendaByDate]
  );

  const selectedLabel = selectionRange
    ? moment(selectionRange).locale(localeCode).format("ddd D MMM YYYY")
    : t("homeDateSelectorPick", "Pick a date");

  return (
    <div
      className={`date-range-picker${expanded ? " is-expanded" : ""}`}
    >
      <button
        type="button"
        className="date-range-picker__toggle"
        aria-expanded={expanded}
        aria-controls="home-date-range-calendar"
        onClick={() => setExpanded((open) => !open)}
      >
        <CalendarMonthOutlinedIcon className="date-range-picker__icon" fontSize="small" />
        <span className="date-range-picker__copy">
          <span className="date-range-picker__kicker">
            {t("homeDateSelectorLabel", "Training sessions by date")}
          </span>
          <span className="date-range-picker__value">{selectedLabel}</span>
        </span>
        <ExpandMoreIcon
          className="date-range-picker__chevron"
          fontSize="small"
          aria-hidden
        />
      </button>
      {expanded ? (
        <div
          id="home-date-range-calendar"
          className="date_info date-range-picker__panel"
        >
          <Calendar
            date={selectionRange}
            onChange={handleselect}
            color={brandColor}
            shownDate={shownDate}
            onShownDateChange={setShownDate}
            dayContentRenderer={dayContentRenderer}
          />
        </div>
      ) : null}
    </div>
  );
};
